import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import OAuthCallback from './pages/OAuthCallback';
import LearnPage from './pages/LearnPage';
import ResetPassword from './pages/ResetPassword';
import {
  PrivacyPage,
  TermsPage,
  DisclaimerPage,
  BillingPolicyPage,
  ContactPage,
} from './pages/public/LegalPages';
import { DealProvider } from './context/DealContext';
import { ThemeModeProvider } from './theme/ThemeModeProvider';
import authService from './services/auth';
import { LEARN_CATEGORIES } from './content/learnCenter';
import HowItWorksPage from './pages/public/HowItWorksPage';
import PricingPage from './pages/public/PricingPage';
import NeighborhoodPage from './pages/public/NeighborhoodPage';
import UnsubscribePage from './pages/public/UnsubscribePage';

const Dashboard = React.lazy(() => import('./pages/protected/Dashboard'));
const NewDeal = React.lazy(() => import('./pages/protected/NewDeal'));
const DealWorkspace = React.lazy(() => import('./pages/protected/DealWorkspace'));
const Profile = React.lazy(() => import('./pages/protected/Profile'));
const UpgradePage = React.lazy(() => import('./pages/protected/Upgrade'));
const ToolsHubPage = React.lazy(() => import('./pages/protected/tools/ToolsHubPage'));
const DealAnalyzerPage = React.lazy(() => import('./pages/protected/tools/DealAnalyzerPage'));
const CompFinderPage = React.lazy(() => import('./pages/protected/tools/CompFinderPage'));
const RiskToolPage = React.lazy(() => import('./pages/protected/tools/RiskToolPage'));
const ReviewToolPage = React.lazy(() => import('./pages/protected/tools/ReviewToolPage'));
const RentalAnalyzerPage = React.lazy(() => import('./pages/protected/tools/RentalAnalyzerPage'));
const BrrrrAnalyzerPage = React.lazy(() => import('./pages/protected/tools/BrrrrAnalyzerPage'));
const AdminOpsPage = React.lazy(() => import('./pages/protected/AdminOpsPage'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = authService.getToken();
  if (!token) {
    return <Navigate to="/login" />;
  }
  return <>{children}</>;
};

const PAGE_TITLES: Record<string, string> = {
  '/': 'DealSkout | Real Estate Deal Analysis',
  '/login': 'DealSkout | Real Estate Deal Analysis',
  '/signup': 'Create your account | DealSkout',
  '/forgot-password': 'Forgot password | DealSkout',
  '/reset-password': 'Reset password | DealSkout',
  '/learn': 'Learning Center | DealSkout',
  '/privacy': 'Privacy Policy | DealSkout',
  '/terms': 'Terms of Service | DealSkout',
  '/disclaimer': 'Investment disclaimer | DealSkout',
  '/billing-policy': 'Billing policy | DealSkout',
  '/contact': 'Contact | DealSkout',
  '/how-it-works': 'How it works | DealSkout',
  '/pricing': 'Pricing | DealSkout',
  '/neighborhood': 'Neighborhood figures | DealSkout',
  '/alerts/unsubscribe': 'Unsubscribe | DealSkout',
  '/dashboard': 'Portfolio | DealSkout',
  '/upgrade': 'Upgrade | DealSkout',
  '/tools': 'Tools | DealSkout',
};

function DocumentHead() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    const slug = pathname.startsWith('/learn/') ? pathname.slice('/learn/'.length) : '';
    const article = LEARN_CATEGORIES.find((category) => category.id === slug);
    const known = article ? `${article.title} | DealSkout` : PAGE_TITLES[pathname];
    document.title = known || (pathname.startsWith('/deals') ? 'Deal | DealSkout' : 'DealSkout | Real Estate Deal Analysis');
    const canonicalPath = pathname === '/login' ? '/' : pathname;
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = `https://thedealskout.com${canonicalPath}`;
  }, [pathname]);

  React.useEffect(() => {
    const host = window.location.hostname;
    if (host === 'thedealskout.com' || host === 'www.thedealskout.com') return undefined;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  return null;
}

const pageFallback = (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress aria-label="Loading" />
  </Box>
);

const App: React.FC = () => {
  return (
    <Router>
      <ThemeModeProvider>
        <DealProvider>
          <DocumentHead />
          <Suspense fallback={pageFallback}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="/learn" element={<LearnPage />} />
              <Route path="/learn/:slug" element={<LearnPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/neighborhood" element={<NeighborhoodPage />} />
              <Route path="/alerts/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/disclaimer" element={<DisclaimerPage />} />
              <Route path="/billing-policy" element={<BillingPolicyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/upgrade"
                element={
                  <ProtectedRoute>
                    <UpgradePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals/new"
                element={
                  <ProtectedRoute>
                    <NewDeal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals/:dealId"
                element={
                  <ProtectedRoute>
                    <DealWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals/:dealId/:section"
                element={
                  <ProtectedRoute>
                    <DealWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools"
                element={
                  <ProtectedRoute>
                    <ToolsHubPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/analyzer"
                element={
                  <ProtectedRoute>
                    <DealAnalyzerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/comps"
                element={
                  <ProtectedRoute>
                    <CompFinderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/risk"
                element={
                  <ProtectedRoute>
                    <RiskToolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/rental-analyzer"
                element={
                  <ProtectedRoute>
                    <RentalAnalyzerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/brrrr-analyzer"
                element={
                  <ProtectedRoute>
                    <BrrrrAnalyzerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/review"
                element={
                  <ProtectedRoute>
                    <ReviewToolPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminOpsPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/calculator" element={<Navigate to="/deals/new" replace />} />
              <Route path="/rehab-calculator" element={<Navigate to="/deals/new" replace />} />
              <Route path="/mortgage-calculator" element={<Navigate to="/deals/new" replace />} />
              <Route path="/market-analysis" element={<Navigate to="/deals/new" replace />} />

              <Route path="/" element={<Login />} />
            </Routes>
          </Suspense>
        </DealProvider>
      </ThemeModeProvider>
    </Router>
  );
};

export default App;
